import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('accessToken');
    if (token) {
      // Verify token by fetching user lists (triggers auth check)
      api.get('/lists')
        .then(() => {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            setUser(JSON.parse(userStr));
          }
          // WICHTIG: Push-Token bei App-Start senden falls vorhanden
          // NICHT ENTFERNEN! Ohne dies werden Tokens nach Neustart nicht mehr gesendet
          sendPendingPushToken();
        })
        .catch(() => {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    console.log('🟡🟡🟡 [LOGIN] Starting...');
    const response = await api.post('/auth/login', { email, password });
    const { user, accessToken, refreshToken } = response.data.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    console.log('🟡 [LOGIN] User authenticated, calling sendPendingPushToken...');

    // Push-Token senden, falls vorhanden
    await sendPendingPushToken();
    console.log('🟢🟢🟢 [LOGIN] Complete');
  };

  const register = async (email: string, password: string, name: string) => {
    const response = await api.post('/auth/register', { email, password, name });
    const { user, accessToken, refreshToken } = response.data.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);

    // Push-Token senden, falls vorhanden
    await sendPendingPushToken();
  };

  const sendPendingPushToken = async () => {
    try {
      console.log('🔵🔵🔵 [PUSH-TOKEN-SEND] Function called');
      const pendingToken = localStorage.getItem('pendingPushToken');
      const currentToken = localStorage.getItem('currentPushToken');
      const accessToken = localStorage.getItem('accessToken');
      
      console.log('🔵 Pending token:', pendingToken ? pendingToken.substring(0, 30) + '...' : 'NONE');
      console.log('🔵 Current token:', currentToken ? currentToken.substring(0, 30) + '...' : 'NONE');
      console.log('🔵 Access token:', accessToken ? 'EXISTS' : 'MISSING');
      
      // Verwende pendingToken (neu) oder currentToken (nach Logout/Login)
      const tokenToSend = pendingToken || currentToken;
      
      if (!tokenToSend) {
        console.log('🔵 No token to send, exiting');
        return;
      }

      console.log('🔵 Sending token to backend...');
      const response = await api.post('/auth/push-token', {
        token: tokenToSend,
        platform: 'android',
        deviceInfo: navigator.userAgent
      });
      
      console.log('🟢🟢🟢 [PUSH-TOKEN-SEND] SUCCESS:', response.data);
      
      // Token erfolgreich gesendet - als current speichern
      localStorage.removeItem('pendingPushToken');
      localStorage.setItem('currentPushToken', tokenToSend);
      console.log('🟢 Token saved as currentPushToken');
      
    } catch (error: any) {
      console.error('🔴🔴🔴 [PUSH-TOKEN-SEND] ERROR:', error);
      console.error('🔴 Error message:', error?.message);
      console.error('🔴 Error response:', error?.response?.data);
      
      // Bei Fehler den Token als pending speichern für späteren Versuch
      const tokenToSave = localStorage.getItem('pendingPushToken') || localStorage.getItem('currentPushToken');
      if (tokenToSave) {
        localStorage.setItem('pendingPushToken', tokenToSave);
        console.log('🔴 Token saved as pending for retry');
      }
    }
  };

  const loginWithGoogle = async (idToken: string) => {
    const response = await api.post('/auth/google', { idToken });
    const { user, accessToken, refreshToken } = response.data.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);

    await sendPendingPushToken();
  };

  const logout = async () => {
    console.log('🔴🔴🔴 [LOGOUT] Starting...');
    const refreshToken = localStorage.getItem('refreshToken');
    const pendingToken = localStorage.getItem('pendingPushToken');
    const currentToken = localStorage.getItem('currentPushToken');
    console.log('🔴 [LOGOUT] pendingPushToken:', pendingToken ? pendingToken.substring(0, 30) + '...' : 'NONE');
    console.log('🔴 [LOGOUT] currentPushToken:', currentToken ? currentToken.substring(0, 30) + '...' : 'NONE');
    
    try {
      await api.post('/auth/logout', { refreshToken });
    } catch (error) {
      console.error('🔴 Logout API error:', error);
    }

    // Push-Token vom Backend löschen
    const pushToken = currentToken;
    if (pushToken) {
      try {
        console.log('🔴 Deleting token from backend:', pushToken.substring(0, 30) + '...');
        await api.delete(`/auth/push-token/${pushToken}`);
        console.log('🔴 Token removed from backend');
      } catch (error) {
        console.error('🔴 Error removing token:', error);
      }
    } else {
      console.log('🔴 No currentPushToken to remove from backend');
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // currentPushToken NICHT löschen - wird beim nächsten Login wiederverwendet
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated: !!user, setUser, login, loginWithGoogle, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
